// src/pages/PlatformAdminIntegraciones.js
import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, addDoc, doc, deleteDoc, updateDoc, query, orderBy, serverTimestamp, onSnapshot, getDoc, setDoc } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import { FaPlug, FaBullhorn, FaTrash, FaEye, FaEyeSlash } from 'react-icons/fa';
import PlatformBackButton from '../components/PlatformBackButton';
import Swal from 'sweetalert2';
import './PlatformAdminIntegraciones.css';

const PlatformAdminIntegraciones = () => {
    const { userData } = useAuth();
    const [actualizaciones, setActualizaciones] = useState([]);
    const [loading, setLoading] = useState(true);

    // Form states
    const [titulo, setTitulo] = useState('');
    const [mensaje, setMensaje] = useState('');
    const [roles, setRoles] = useState({
        admin: false,
        residente: false,
        caseta: false
    });
    const [enviarATodos, setEnviarATodos] = useState(true);
    const [incluirEncuesta, setIncluirEncuesta] = useState(true);
    const [alertaActiva, setAlertaActiva] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [expandedItems, setExpandedItems] = useState(new Set());

    // List & Pagination states
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const ITEMS_PER_PAGE = 5;

    useEffect(() => {
        setLoading(true);
        const q = query(collection(db, 'actualizaciones'), orderBy('fechaEmision', 'desc'));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const data = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            console.log("[PlatformAdminIntegraciones] Nuevos datos recibidos desde Firestore (onSnapshot):", data);
            setActualizaciones(data);
            setLoading(false);
        }, (error) => {
            console.error("Error cargando actualizaciones en tiempo real:", error);
            setLoading(false);
        });

        // Limpiar subscripción al desmontar
    }, []);

    useEffect(() => {
        const fetchConfigGlobal = async () => {
            try {
                const configRef = doc(db, 'plataforma', 'config_global');
                const docSnap = await getDoc(configRef);
                if (docSnap.exists()) {
                    const data = docSnap.data();
                    setAlertaActiva(Boolean(data.alertaMejorasActiva));
                }
            } catch (error) {
                console.error('Error al cargar config_global:', error);
            }
        };
        fetchConfigGlobal();
    }, []);

    // List processing
    const filteredActualizaciones = actualizaciones.filter(item => {
        const itemMsg = item.mensaje || item.descripcion || '';
        return (item.titulo || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
               itemMsg.toLowerCase().includes(searchTerm.toLowerCase());
    });

    const totalPages = Math.ceil(filteredActualizaciones.length / ITEMS_PER_PAGE);
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const currentItems = filteredActualizaciones.slice(startIndex, startIndex + ITEMS_PER_PAGE);

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1);
    };

    const handleRoleChange = (e) => {
        const { name, checked } = e.target;
        setRoles(prev => ({
            ...prev,
            [name]: checked
        }));
    };

    const handlePublish = async (e) => {
        e.preventDefault();

        // Si se seleccionó "Todos", forzamos el arreglo a tener los 3 roles
        const selectedRoles = enviarATodos
            ? ['admin', 'residente', 'caseta']
            : Object.keys(roles).filter(role => roles[role]);

        if (!titulo || !mensaje || selectedRoles.length === 0) {
            Swal.fire({
                icon: 'warning',
                title: 'Campos incompletos',
                text: 'Por favor completa el título, mensaje y selecciona al menos un rol destino.',
                confirmButtonColor: '#3b82f6'
            });
            return;
        }

        setIsSubmitting(true);
        try {
            await addDoc(collection(db, 'actualizaciones'), {
                titulo: titulo,
                mensaje: mensaje,
                fechaEmision: serverTimestamp(),
                autor: userData?.email || 'Platform Admin',
                rolesDestino: selectedRoles,
                incluirEncuesta: incluirEncuesta,
                activo: true,
                stats: incluirEncuesta ? { likes: 0, doubts: 0, dislikes: 0 } : null
            });

            // Guardar también en la configuración global para alertar al administrador local
            const configRef = doc(db, 'plataforma', 'config_global');
            const timestampVersion = Date.now().toString();
            const rolesAlerta = {
                administrador: selectedRoles.includes('admin'),
                residente: selectedRoles.includes('residente'),
                caseta: selectedRoles.includes('caseta')
            };

            await setDoc(configRef, {
                alertaMejorasActiva: alertaActiva,
                alertaMejorasTitulo: titulo.trim(),
                alertaMejorasMensaje: mensaje.trim(),
                alertaMejorasVersion: timestampVersion,
                rolesAlerta: rolesAlerta,
                updatedAt: new Date().toISOString()
            }, { merge: true });

            // Refrescar y limpiar
            setTitulo('');
            setMensaje('');
            setRoles({ admin: false, residente: false, caseta: false });
            setEnviarATodos(true);
            setIncluirEncuesta(true);
            setAlertaActiva(false);

            Swal.fire({
                icon: 'success',
                title: '¡Publicado!',
                text: 'La actualización se ha enviado correctamente y se actualizó la alerta global.',
                timer: 2000,
                showConfirmButton: false
            });

        } catch (error) {
            console.error("Error al publicar:", error);
            Swal.fire({
                icon: 'error',
                title: 'Error de publicación',
                text: 'Hubo un error al guardar la actualización en la base de datos.',
                confirmButtonColor: '#ef4444'
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const toggleActivo = async (item) => {
        try {
            const itemRef = doc(db, 'actualizaciones', item.id);
            await updateDoc(itemRef, {
                activo: !item.activo
            });
        } catch (error) {
            console.error("Error al cambiar estado:", error);
        }
    };

    const toggleExpand = (id) => {
        setExpandedItems(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const handleDelete = async (id) => {
        const result = await Swal.fire({
            title: '¿Eliminar actualización?',
            text: "Esta acción no se puede deshacer y desparecerá el historial de encuestas relacionadas a esta nota.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#ef4444',
            cancelButtonColor: '#6b7280',
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar'
        });

        if (result.isConfirmed) {
            try {
                await deleteDoc(doc(db, 'actualizaciones', id));
                Swal.fire({
                    icon: 'success',
                    title: 'Eliminada',
                    text: 'La actualización fue borrada permanentemente.',
                    timer: 1500,
                    showConfirmButton: false
                });
            } catch (error) {
                console.error("Error al eliminar:", error);
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'No se pudo eliminar la actualización.',
                    confirmButtonColor: '#ef4444'
                });
            }
        }
    };

    return (
        <div className="platform-integraciones-container">
            <div className="platform-config-card header-card" style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <div className="header-icon-box purple">
                        <FaPlug />
                    </div>
                    <div className="header-info-text">
                        <h1>Integraciones y Novedades</h1>
                        <p>Gestiona los correos, pasarelas de pago y publica novedades de la plataforma (Changelogs).</p>
                    </div>
                </div>
                <div className="header-actions-right" style={{ alignSelf: 'center', margin: 0 }}>
                    <PlatformBackButton />
                </div>
            </div>

            <section className="platform-config-card" style={{ paddingTop: '1.5rem' }}>
                <form className="changelog-form" onSubmit={handlePublish}>
                    <div className="changelog-form-left">
                        <div className="form-group">
                            <label>Título de la Actualización (Negrita)</label>
                            <input
                                type="text"
                                className="input-premium"
                                placeholder="Ej: Nuevos recibos de cobro por lote..."
                                value={titulo}
                                onChange={(e) => setTitulo(e.target.value)}
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label>Mensaje / Detalle (Texto Plano)</label>
                            <textarea
                                className="input-premium textarea-premium"
                                placeholder="Describe qué cambios se hicieron..."
                                rows="6"
                                value={mensaje}
                                onChange={(e) => setMensaje(e.target.value)}
                                required
                            />
                        </div>
                    </div>

                    <div className="changelog-form-right" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        {/* Grupo 1: Roles Destinatarios */}
                        <div className="form-group" style={{ background: '#f8fafc', padding: '14px', borderRadius: '12px', border: '1px solid #e2e8f0', margin: 0 }}>
                            <label style={{ fontSize: '0.8rem', fontWeight: '800', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.03em', marginBottom: '8px', display: 'block' }}>
                                Roles Destino (Quién lo verá)
                            </label>
                            <div className="roles-checkbox-group" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                <label className="integ-checkbox-label" style={{ fontWeight: '700', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        checked={enviarATodos}
                                        onChange={(e) => setEnviarATodos(e.target.checked)}
                                        style={{ width: '17px', height: '17px', accentColor: '#3b82f6', cursor: 'pointer' }}
                                    />
                                    Todos los roles
                                </label>

                                {!enviarATodos && (
                                    <div className="sub-roles-group" style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', marginTop: '4px', paddingLeft: '4px' }}>
                                        <label className="integ-checkbox-label" style={{ fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                                            <input type="checkbox" name="admin" checked={roles.admin} onChange={handleRoleChange} style={{ width: '16px', height: '16px', accentColor: '#3b82f6', cursor: 'pointer' }} />
                                            Administrador
                                        </label>
                                        <label className="integ-checkbox-label" style={{ fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                                            <input type="checkbox" name="residente" checked={roles.residente} onChange={handleRoleChange} style={{ width: '16px', height: '16px', accentColor: '#3b82f6', cursor: 'pointer' }} />
                                            Residente
                                        </label>
                                        <label className="integ-checkbox-label" style={{ fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                                            <input type="checkbox" name="caseta" checked={roles.caseta} onChange={handleRoleChange} style={{ width: '16px', height: '16px', accentColor: '#3b82f6', cursor: 'pointer' }} />
                                            Caseta
                                        </label>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Grupo 2: Opciones de Publicación y Alerta */}
                        <div className="form-group" style={{ background: '#f8fafc', padding: '14px', borderRadius: '12px', border: '1px solid #e2e8f0', margin: 0, display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            <label style={{ fontSize: '0.8rem', fontWeight: '800', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.03em', display: 'block', marginBottom: '2px' }}>
                                Opciones adicionales
                            </label>
                            
                            <label className="integ-checkbox-label" style={{ fontWeight: '600', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                <input
                                    type="checkbox"
                                    checked={incluirEncuesta}
                                    onChange={(e) => setIncluirEncuesta(e.target.checked)}
                                    style={{ width: '17px', height: '17px', accentColor: '#3b82f6', cursor: 'pointer' }}
                                />
                                Incluir encuesta de opinión
                            </label>

                            <label className="integ-checkbox-label" style={{ fontWeight: '600', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', color: alertaActiva ? '#0284c7' : 'inherit' }}>
                                <input
                                    type="checkbox"
                                    checked={alertaActiva}
                                    onChange={(e) => setAlertaActiva(e.target.checked)}
                                    style={{ width: '17px', height: '17px', accentColor: '#0284c7', cursor: 'pointer' }}
                                />
                                Forzar alerta emergente de recarga (Actualizar pantalla)
                            </label>
                        </div>

                        <button type="submit" className="btn-publish" disabled={isSubmitting} style={{ marginTop: 'auto', width: '100%', padding: '12px', fontWeight: '700', borderRadius: '10px' }}>
                            {isSubmitting ? 'Publicando...' : 'Publicar Actualización'}
                        </button>
                    </div>
                </form>
            </section>

            <section className="platform-config-card">
                <h3>Historial de Changelogs Públicos</h3>

                <div className="cl-toolbar">
                    <input
                        type="search"
                        placeholder="Buscar por título o contenido..."
                        className="cl-search-input"
                        value={searchTerm}
                        onChange={handleSearchChange}
                    />
                </div>

                {loading ? (
                    <p>Cargando historial...</p>
                ) : filteredActualizaciones.length === 0 ? (
                    <p>No hay actualizaciones que coincidan con la búsqueda.</p>
                ) : (
                    <>
                        <div className="changelog-list">
                            {currentItems.map(item => {
                                const isExpanded = expandedItems.has(item.id);
                                const itemMsg = item.mensaje || item.descripcion || '';
                                const shouldTruncate = itemMsg.length > 250;
                                const displayMensaje = (shouldTruncate && !isExpanded) 
                                    ? itemMsg.substring(0, 250) + '...' 
                                    : itemMsg;

                                return (
                                    <div key={item.id} className={`changelog-item ${!item.activo ? 'inactive' : ''}`}>
                                        <div className="cl-info">
                                            <h4>{item.titulo}</h4>
                                            <p className="cl-fecha">
                                                {item.fechaEmision?.toDate ? item.fechaEmision.toDate().toLocaleDateString('es-MX') : 'Reciente'} - Dirigido a: {item.rolesDestino?.join(', ')}
                                            </p>
                                            <p className="cl-mensaje">{displayMensaje}</p>
                                            
                                            {shouldTruncate && (
                                                <button 
                                                    type="button" 
                                                    className="cl-btn-see-more"
                                                    onClick={() => toggleExpand(item.id)}
                                                >
                                                    {isExpanded ? 'Ver menos' : 'Ver más'}
                                                </button>
                                            )}

                                            <div className="cl-footer-row">
                                                <div className="cl-actions-minimal">
                                                    <button
                                                        type="button"
                                                        className="cl-btn-toggle-v2"
                                                        title={item.activo ? 'Ocultar a todos' : 'Mostrar a todos'}
                                                        onClick={() => toggleActivo(item)}
                                                    >
                                                        {item.activo ? <FaEyeSlash /> : <FaEye />}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="cl-btn-delete-v2"
                                                        title="Eliminar"
                                                        onClick={() => handleDelete(item.id)}
                                                    >
                                                        <FaTrash />
                                                    </button>
                                                </div>

                                                {item.incluirEncuesta && (
                                                    <div className="cl-stats-v2">
                                                        <span title="Me gusta">👍 {(item.stats?.likes || 0) + (item.stats?.like || 0)}</span>
                                                        <span title="Duda">🧐 {(item.stats?.doubts || 0) + (item.stats?.doubt || 0)}</span>
                                                        <span title="No me gusta">👎 {(item.stats?.dislikes || 0) + (item.stats?.dislike || 0)}</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {totalPages > 1 && (
                            <div className="cl-pagination">
                                <button
                                    className="cl-page-btn"
                                    disabled={currentPage === 1}
                                    onClick={() => setCurrentPage(p => p - 1)}
                                >
                                    Anterior
                                </button>
                                <span className="cl-page-indicator">Página {currentPage} de {totalPages}</span>
                                <button
                                    className="cl-page-btn"
                                    disabled={currentPage === totalPages}
                                    onClick={() => setCurrentPage(p => p + 1)}
                                >
                                    Siguiente
                                </button>
                            </div>
                        )}
                    </>
                )}
            </section>
        </div>
    );
};

export default PlatformAdminIntegraciones;
